// New composition-based view system
// Replaces the old monolithic ViewMode approach

export type Layout = 'list' | 'small' | 'medium' | 'large';

export type CollectionType = 'generic' | 'checklist' | 'stock' | 'stock-checklist' | 'marketplace';

export interface ViewConfig {
  layout: Layout;
  collectionType: CollectionType;
}

// Simplified: One size choice controls everything
export type ThumbnailSize = 'small' | 'medium' | 'large' | 'x-large';

export interface ItemFeatures {
  showCheckbox: boolean;
  showQuantity: boolean;
  showPrice: boolean;
  showDetails: boolean; // Categories, location, etc.
  showShareBadge: boolean;
}

// Size determines layout AND what details to show
export function sizeToLayout(size: ThumbnailSize): Layout {
  switch (size) {
    case 'small':
      return 'list'; // Horizontal list with small thumbnails (96px)
    case 'medium':
      return 'list'; // Horizontal list with medium thumbnails (110px)
    case 'large':
      return 'list'; // Horizontal list with large thumbnails (160px)
    case 'x-large':
      return 'large'; // Grid with name only, single column
  }
}

// Helper to determine features based on collection type and size
export function getFeatures(collectionType: CollectionType, size: ThumbnailSize): ItemFeatures {
  const features = {
    showCheckbox: collectionType.includes('checklist'),
    showQuantity: collectionType.includes('stock') || collectionType === 'marketplace',
    showPrice: collectionType === 'marketplace',
    showDetails: size === 'small' || size === 'medium' || size === 'large', // Show details for all list layouts
    showShareBadge: collectionType !== 'marketplace', // Hide for marketplace
  };
  
  return features;
}

// Helper to determine thumbnail size from layout (for backward compatibility)
export function getThumbnailSize(layout: Layout): ThumbnailSize {
  switch (layout) {
    case 'list':
      return 'small';
    case 'small':
      return 'small';
    case 'medium':
      return 'medium';
    case 'large':
      return 'large';
  }
}

// Backward compatibility: Convert old ViewMode to new ViewConfig
export function viewModeToConfig(viewMode: string): ViewConfig {
  const mapping: Record<string, ViewConfig> = {
    'list': { layout: 'list', collectionType: 'generic' },
    'card': { layout: 'medium', collectionType: 'generic' },
    'checklist-list': { layout: 'list', collectionType: 'checklist' },
    'checklist-card': { layout: 'medium', collectionType: 'checklist' },
    'stock-list': { layout: 'list', collectionType: 'stock' },
    'stock-card': { layout: 'medium', collectionType: 'stock' },
    'stock-checklist-list': { layout: 'list', collectionType: 'stock-checklist' },
    'stock-checklist-card': { layout: 'medium', collectionType: 'stock-checklist' },
    'marketplace-list': { layout: 'list', collectionType: 'marketplace' },
    'marketplace-card': { layout: 'medium', collectionType: 'marketplace' },
  };
  
  return mapping[viewMode] || { layout: 'medium', collectionType: 'generic' };
}

// Forward compatibility: Convert ViewConfig back to ViewMode (for API calls)
export function configToViewMode(config: ViewConfig): string {
  const { layout, collectionType } = config;
  
  // Determine if it's list or card
  const layoutSuffix = layout === 'list' ? 'list' : 'card';
  
  // Build view mode string
  if (collectionType === 'generic') {
    return layoutSuffix;
  }
  
  return `${collectionType}-${layoutSuffix}`;
}
